import cx from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";

type CellAnchorComment = {
  _id?: string;
  description?: string;
  cellAnchor?: {
    sheetIndex: number;
    sheetName?: string;
    range: string;
  };
  disposition?: string;
};

export type SpreadsheetViewProps = {
  url: string;
  comments?: CellAnchorComment[];
  activeSheetIndex?: number;
  onSheetChange?: (sheetIndex: number) => void;
  onCommentClick?: (commentId: string) => void;
};

type Sheet = {
  name: string;
  cells: unknown[][];
  colCount: number;
};

type ParsedWorkbook = {
  sheets: Sheet[];
};

const MAX_RENDER_ROWS = 1000;
const MAX_RENDER_COLS = 50;

function colLetter(colIndex: number): string {
  let n = colIndex + 1;
  let letters = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
}

function parseA1(ref: string): { col: number; row: number } | null {
  const m = /^([A-Z]+)(\d+)$/.exec(ref.toUpperCase());
  if (!m) return null;
  const letters = m[1];
  const row = parseInt(m[2], 10) - 1;
  let col = 0;
  for (const ch of letters) {
    col = col * 26 + (ch.charCodeAt(0) - 64);
  }
  return { col: col - 1, row };
}

function parseRange(
  range: string,
): { minCol: number; minRow: number; maxCol: number; maxRow: number } | null {
  const parts = range.replace(/\s+/g, "").split(":");
  if (parts.length === 1) {
    const p = parseA1(parts[0]);
    if (!p) return null;
    return { minCol: p.col, minRow: p.row, maxCol: p.col, maxRow: p.row };
  }
  const a = parseA1(parts[0]);
  const b = parseA1(parts[1]);
  if (!a || !b) return null;
  return {
    minCol: Math.min(a.col, b.col),
    minRow: Math.min(a.row, b.row),
    maxCol: Math.max(a.col, b.col),
    maxRow: Math.max(a.row, b.row),
  };
}

export function SpreadsheetView(props: SpreadsheetViewProps) {
  const { url, comments, activeSheetIndex, onSheetChange, onCommentClick } =
    props;

  const [workbook, setWorkbook] = useState<ParsedWorkbook | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [localActiveSheet, setLocalActiveSheet] = useState<number>(
    activeSheetIndex ?? 0,
  );
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof activeSheetIndex === "number") {
      setLocalActiveSheet(activeSheetIndex);
    }
  }, [activeSheetIndex]);

  useEffect(() => {
    let cancelled = false;
    const fetchAndParse = async () => {
      setError(null);
      setWorkbook(null);
      try {
        const resp = await fetch(url);
        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`);
        }
        const buf = await resp.arrayBuffer();
        const XLSX = await import("xlsx");
        const wb = XLSX.read(buf, { type: "array", cellFormula: false });
        if (cancelled) return;
        const sheets: Sheet[] = wb.SheetNames.map((name: string) => {
          const ws = wb.Sheets[name];
          const aoa = XLSX.utils.sheet_to_json(ws, {
            header: 1,
            blankrows: true,
            defval: undefined,
          }) as unknown[][];
          const truncated = aoa
            .slice(0, MAX_RENDER_ROWS)
            .map((row) => (row ?? []).slice(0, MAX_RENDER_COLS));
          const colCount = truncated.reduce(
            (max, row) => Math.max(max, row.length),
            0,
          );
          return { name, cells: truncated, colCount };
        });
        setWorkbook({ sheets });
      } catch (err) {
        if (cancelled) return;
        setError((err as Error).message);
      }
    };
    fetchAndParse();
    return () => {
      cancelled = true;
    };
  }, [url]);

  const overlaysBySheet = useMemo(() => {
    const map = new Map<
      number,
      {
        commentId: string;
        rangeStr: string;
        bounds: ReturnType<typeof parseRange>;
        description: string;
      }[]
    >();
    for (const c of comments ?? []) {
      if (!c.cellAnchor) continue;
      if (c.disposition === "dismissed") continue;
      const bounds = parseRange(c.cellAnchor.range);
      if (!bounds) continue;
      const list = map.get(c.cellAnchor.sheetIndex) ?? [];
      list.push({
        commentId: c._id ?? "",
        rangeStr: c.cellAnchor.range,
        bounds,
        description: c.description ?? "",
      });
      map.set(c.cellAnchor.sheetIndex, list);
    }
    return map;
  }, [comments]);

  const overlayKey = (sheetIdx: number, col: number, row: number) =>
    `${sheetIdx}:${col}:${row}`;

  const overlayCells = useMemo(() => {
    const set = new Map<string, { commentId: string; description: string }>();
    overlaysBySheet.forEach((list, sheetIdx) => {
      for (const o of list) {
        if (!o.bounds) continue;
        for (let r = o.bounds.minRow; r <= o.bounds.maxRow; r++) {
          for (let c = o.bounds.minCol; c <= o.bounds.maxCol; c++) {
            set.set(overlayKey(sheetIdx, c, r), {
              commentId: o.commentId,
              description: o.description,
            });
          }
        }
      }
    });
    return set;
  }, [overlaysBySheet]);

  const handleSheetClick = (idx: number) => {
    setLocalActiveSheet(idx);
    onSheetChange?.(idx);
  };

  if (error) {
    return (
      <div className="p-4 text-puntt-neutral-gray-11">
        <p>Could not render spreadsheet: {error}</p>
        <a
          data-testid="spreadsheet-view-download-link"
          href={url}
          target="_blank"
          rel="noreferrer"
          className="text-puntt-accent-12 hover:text-puntt-accent-11 active:text-puntt-accent-10"
        >
          Download instead
        </a>
      </div>
    );
  }

  if (!workbook) {
    return <div className="p-4">Loading spreadsheet…</div>;
  }

  const sheet = workbook.sheets[localActiveSheet] ?? workbook.sheets[0];
  if (!sheet) {
    return <div className="p-4">No sheets in this workbook.</div>;
  }

  return (
    <div className="flex size-full flex-col">
      <div
        ref={containerRef}
        className="border-puntt-neutral-6 flex-1 overflow-auto border"
      >
        <table className="min-w-full border-collapse text-sm">
          <thead className="bg-puntt-neutral-2 sticky top-0 z-10">
            <tr>
              <th className="bg-puntt-neutral-3 border-puntt-neutral-6 font-mono sticky left-0 w-10 border px-2 py-1 text-center text-xs">
                &nbsp;
              </th>
              {Array.from({ length: sheet.colCount }).map((_, c) => (
                <th
                  key={c}
                  className="border-puntt-neutral-6 font-mono border px-2 py-1 text-center text-xs"
                >
                  {colLetter(c)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sheet.cells.map((row, rIdx) => (
              <tr key={rIdx}>
                <th className="bg-puntt-neutral-3 border-puntt-neutral-6 font-mono sticky left-0 w-10 border px-2 py-1 text-center text-xs">
                  {rIdx + 1}
                </th>
                {Array.from({ length: sheet.colCount }).map((_, cIdx) => {
                  const value = row[cIdx];
                  const overlay = overlayCells.get(
                    overlayKey(localActiveSheet, cIdx, rIdx),
                  );
                  return (
                    <td
                      data-testid={
                        overlay
                          ? `spreadsheet-cell-comment-${colLetter(cIdx)}${rIdx + 1}`
                          : undefined
                      }
                      key={cIdx}
                      title={overlay ? overlay.description : undefined}
                      onClick={
                        overlay && overlay.commentId
                          ? () => onCommentClick?.(overlay.commentId)
                          : undefined
                      }
                      className={cx(
                        "border-puntt-neutral-6 whitespace-nowrap border px-2 py-1",
                        overlay &&
                          "cursor-pointer bg-puntt-accent-3 outline outline-2 outline-puntt-accent-9",
                      )}
                    >
                      {value === undefined || value === null
                        ? ""
                        : String(value)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {workbook.sheets.length > 1 && (
        <div className="border-puntt-neutral-6 bg-puntt-neutral-2 flex gap-1 border-t px-2 py-1">
          {workbook.sheets.map((s, idx) => {
            const overlayCount = overlaysBySheet.get(idx)?.length ?? 0;
            const isActive = idx === localActiveSheet;
            return (
              <button
                data-testid={`spreadsheet-sheet-tab-${idx}`}
                key={idx}
                type="button"
                onClick={() => handleSheetClick(idx)}
                className={cx(
                  "rounded border px-3 py-1 text-xs",
                  isActive
                    ? "border-puntt-accent-9 bg-puntt-accent-3 text-puntt-accent-12"
                    : "bg-puntt-neutral-1 border-puntt-neutral-6 hover:bg-puntt-neutral-3",
                )}
              >
                {s.name}
                {overlayCount > 0 && (
                  <span className="text-puntt-neutral-1 ml-2 inline-block rounded-full bg-puntt-accent-9 px-1.5">
                    {overlayCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
